import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { dismissToast, getToasts, subscribe, type ToastItem } from './toastStore';

/**
 * Rendu des notifications ephemeres.
 *
 * Monté **une seule fois dans `main.tsx`**, a cote de `<App>` : il survit donc aux navigations
 * Inertia. Consequence a ne jamais oublier : il est **hors du contexte Inertia**, il ne doit donc
 * jamais appeler `usePage()` (c'est le role de `useFlashToasts`, appele depuis le layout).
 */
const ICONS: Record<ToastItem['kind'], string> = {
    success: 'fas fa-circle-check',
    error: 'fas fa-exclamation-triangle',
    info: 'fas fa-info-circle',
};

/**
 * Classes de variante ecrites **en clair** : dans `@layer components`, Tailwind elague toute classe
 * qu'il ne voit pas litteralement dans les sources. Une classe composee (`toast-${kind}`) disparait
 * donc du CSS final — le toast se retrouvait sans fond (transparent).
 */
const KIND_CLASSES: Record<ToastItem['kind'], string> = {
    success: 'toast-success',
    error: 'toast-error',
    info: 'toast-info',
};

/** Une notification : disparait seule apres sa duree, laisse plus de temps au survol. */
const ToastCard = ({ item, closeLabel }: { item: ToastItem; closeLabel: string }) => {
    const [remaining, setRemaining] = useState(item.duration);
    const [paused, setPaused] = useState(false);
    const startedAt = useRef(Date.now());

    useEffect(() => {
        if (paused) {
            return;
        }

        startedAt.current = Date.now();
        const timer = window.setTimeout(() => dismissToast(item.id), remaining);

        return () => window.clearTimeout(timer);
    }, [item.id, paused, remaining]);

    const pause = (): void => {
        setRemaining((current) => Math.max(0, current - (Date.now() - startedAt.current)));
        setPaused(true);
    };

    return (
        <div
            className={`toast ${KIND_CLASSES[item.kind]}`}
            role="status"
            aria-live={item.kind === 'error' ? 'assertive' : 'polite'}
            onMouseEnter={pause}
            onMouseLeave={() => setPaused(false)}
        >
            <i className={`${ICONS[item.kind]} toast-icon`} aria-hidden="true" />
            <p>{item.message}</p>
            <button type="button" className="toast-close" aria-label={closeLabel} onClick={() => dismissToast(item.id)}>
                <i className="fas fa-xmark" aria-hidden="true" />
            </button>
        </div>
    );
};

const ToastHost = () => {
    const items = useSyncExternalStore(subscribe, getToasts);
    const { t } = useTranslation();

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        <div className="toast-viewport">
            {items.map((item) => (
                <ToastCard key={item.id} item={item} closeLabel={t('toast.dismiss')} />
            ))}
        </div>,
        document.body,
    );
};

export default ToastHost;
